import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Braces } from 'lucide-react'
import { useAvailableProperties } from '@/hooks/useApi'
import { BUILTIN_VARIABLES, sanitizeTokenFallback, type TemplateVariable } from '@/lib/templateVariables'

interface VariableDropdownProps {
  onInsert: (variable: TemplateVariable, fallback?: string) => void
}

export function VariableDropdown({ onInsert }: VariableDropdownProps) {
  const { t } = useTranslation('sequences')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [fallback, setFallback] = useState('')
  const { data: dynamicProperties } = useAvailableProperties()

  const variables = useMemo(() => {
    const dynamic: TemplateVariable[] = (dynamicProperties || [])
      .filter((p) => !p.builtin)
      .map((p) => ({
        key: p.key,
        label: p.label,
        builtin: false,
        coverage: p.coverage,
      }))
    return [...BUILTIN_VARIABLES, ...dynamic]
  }, [dynamicProperties])

  const filtered = useMemo(() => {
    if (!search) return variables
    const q = search.toLowerCase()
    return variables.filter(
      (v) => v.key.toLowerCase().includes(q) || v.label.toLowerCase().includes(q),
    )
  }, [variables, search])

  const { builtinFiltered, dynamicFiltered } = useMemo(() => ({
    builtinFiltered: filtered.filter((v) => v.builtin),
    dynamicFiltered: filtered.filter((v) => !v.builtin),
  }), [filtered])

  // Ricerca e fallback valgono per la singola inserzione: alla chiusura del
  // popover ripartono da zero, anche se l'utente esce senza scegliere nulla.
  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setSearch('')
      setFallback('')
    }
  }

  const handleSelect = (variable: TemplateVariable) => {
    // L'apostrofo dritto è il delimitatore della grammatica {{var | 'testo'}}:
    // nel fallback diventa apostrofo tipografico per non troncare il token.
    const cleaned = sanitizeTokenFallback(fallback.trim())
    onInsert(variable, cleaned || undefined)
    handleOpenChange(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-xs">
          <Braces className="h-3.5 w-3.5" />
          {t('variables.insert')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <Input
          placeholder={t('variables.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-7 text-xs mb-2"
          autoFocus
        />
        <div className="mb-2">
          <Input
            placeholder={t('variables.fallbackPlaceholder')}
            value={fallback}
            onChange={(e) => setFallback(e.target.value)}
            className="h-7 text-xs"
          />
          <p className="text-[10px] text-muted-foreground mt-1 px-1">
            {t('variables.fallbackHint')}
          </p>
        </div>
        <div className="max-h-48 overflow-y-auto space-y-1">
          {builtinFiltered.length > 0 && (
            <>
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-1">
                {t('variables.builtin')}
              </p>
              {builtinFiltered.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className="w-full text-left px-2 py-1 text-xs rounded hover:bg-accent cursor-pointer flex items-center justify-between"
                  onClick={() => handleSelect(v)}
                >
                  <span>{v.label}</span>
                  <code className="text-[10px] text-muted-foreground">{`{{${v.key}}}`}</code>
                </button>
              ))}
            </>
          )}
          {dynamicFiltered.length > 0 && (
            <>
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-1 mt-2">
                {t('variables.hubspotProperties')}
              </p>
              {dynamicFiltered.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className="w-full text-left px-2 py-1 text-xs rounded hover:bg-accent cursor-pointer flex items-center justify-between"
                  onClick={() => handleSelect(v)}
                >
                  <span>{v.label}</span>
                  <span className="text-[10px] text-muted-foreground">{v.coverage}%</span>
                </button>
              ))}
            </>
          )}
          {filtered.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">{t('variables.noResults')}</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
